import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {
    IEmailDisconnectCommand,
    IEmailConversationEvent,
    IEmailConversationInfoEvent,
    IEmailErrorEvent,
    IInboundEmailEvent,
    IOutboundEmail,
    ISendMessageResult,
    SendType,
} from '@dxp/core/models';
import { environment } from '@dxp/core/environment';
import { CommunicationHubService } from '../communication.hub.service';
import { RETRY_INTERVAL_MS, RETRY_LIMIT } from '../../signalr.hub.service';

@Injectable({
    providedIn: 'root',
})
export class EmailHubService extends CommunicationHubService {
    readonly #emailMessageSubject = new Subject<IInboundEmailEvent>();
    readonly #conversationInfoSubject =
        new Subject<IEmailConversationInfoEvent>();
    readonly #errorSubject = new Subject<IEmailErrorEvent>();

    emailMessage$ = this.#emailMessageSubject.asObservable();
    conversationInfo$ = this.#conversationInfoSubject.asObservable();
    error$ = this.#errorSubject.asObservable();

    name = 'Email';
    url = `${environment.apiBaseUrl}/email/hub`;

    sendDisconnect(command: IEmailDisconnectCommand) {
        this.send('Disconnect', command);
    }

    acceptEmail(conversationId: string) {
        this.hubConnection?.send('Accept', { conversationId });
    }

    peekOrConnect(conversationId: string) {
        this.hubConnection?.send('PeekOrConnect', { conversationId });
    }

    rejectEmail(conversationId: string) {
        this.hubConnection?.send('Reject', { conversationId });
    }

    sendEmail(
        conversationId: string,
        sendType: SendType,
        email: IOutboundEmail,
    ): Observable<ISendMessageResult> {
        return this.invoke(sendType, { ...email, conversationId });
    }

    protected override registerHandlers() {
        this.hubConnection.on(
            'ReceiveEmailMessage',
            (emailMessage: IInboundEmailEvent) => {
                this.messageReceived();

                this.logger.debug(
                    'Email Hub Service (ReceiveEmailMessage) ->',
                    emailMessage,
                );
                this.logger.table(emailMessage);

                this.#emailMessageSubject.next(emailMessage);
            },
        );

        this.hubConnection.on(
            'RequestRefresh',
            (requestRefresh: IEmailConversationEvent) => {
                this.messageReceived();

                this.logger.debug(
                    'Email Hub Service (RequestRefresh) ->',
                    requestRefresh,
                );
                this.logger.table(requestRefresh);

                this.peekOrConnect(requestRefresh.conversationId);
            },
        );

        this.hubConnection.on(
            'ReceiveConversationInfo',
            (receiveConversationInfo: IEmailConversationInfoEvent) => {
                this.messageReceived();

                this.logger.debug(
                    'Email Hub Service (ReceiveConversationInfo) ->',
                    receiveConversationInfo,
                );
                this.logger.table(receiveConversationInfo);

                this.#conversationInfoSubject.next(receiveConversationInfo);
            },
        );

        this.hubConnection.on(
            'ReceiveError',
            (receiveError: IEmailErrorEvent) => {
                this.messageReceived();

                this.logger.error(
                    'Email Hub Service (ReceiveError) ->',
                    receiveError,
                );
                this.logger.table(receiveError);

                const { conversationId, errorReason } = receiveError;

                if (errorReason === 'NotHandlingAgent') {
                    this.tryReconnectToConversation(
                        conversationId,
                        () => this.peekOrConnect(conversationId),
                        { delayMs: RETRY_INTERVAL_MS, maxRetries: RETRY_LIMIT },
                        () => {
                            this.logger.warn(
                                `Max retries reached for ${conversationId}`,
                            );
                        },
                    );
                }

                this.#errorSubject.next(receiveError);
            },
        );
    }
    protected override unregisterHandlers() {
        this.hubConnection.off('ReceiveEmailMessage');
        this.hubConnection.off('RequestRefresh');
        this.hubConnection.off('ReceiveConversationInfo');
        this.hubConnection.off('ReceiveError');
    }
}
